import * as Highcharts from 'highcharts';
import { BaseChartProps, SeriesElementClickHandler, YAxisConfigArrayItem } from '../types/chartProps';
import { getYAxisIdConfigMap, getYAxisIdIndexMap, isPrimaryYAxisIndex } from './axisUtils';
import { isValidStackingValue } from './stackingUtils';

export function resolveChartTypeByYAxisIndex(
  defaultChartType: Highcharts.SeriesOptionsType['type'],
  axisIndex: number
): Highcharts.SeriesOptionsType['type'] {
  if (isPrimaryYAxisIndex(axisIndex)) {
    return defaultChartType;
  }

  // determine chart type for secondary yAxis series
  switch (defaultChartType) {
    case 'column':
      return 'line';
    case 'line':
      return 'column';
    default:
      return defaultChartType;
  }
}

export function prepareSeriesDataForMultipleYAxis<T extends Highcharts.SeriesOptionsType>(
  params: BaseChartProps,
  primaryChartType: T['type']
): T[] {
  const yAxisIdIndexMap = getYAxisIdIndexMap(params.yAxis as YAxisConfigArrayItem[]);
  const yAxisIdConfigMap = getYAxisIdConfigMap(params.yAxis as YAxisConfigArrayItem[]);
  return params.series.map((el, index) => {
    const yAxisIndex = yAxisIdIndexMap[el.yAxisId ?? ''] ?? 0;
    const yAxisConfig = yAxisIdConfigMap[el.yAxisId ?? ''];
    const chartType = resolveChartTypeByYAxisIndex(primaryChartType, yAxisIndex);
    return {
      type: chartType,
      yAxis: el.yAxisId,
      name: el.name ?? params.categories[index],
      custom: {
        displayName: el.displayName,
        id: el.id,
        isPrimaryYAxis: isPrimaryYAxisIndex(yAxisIndex),
        valueDisplayType: yAxisConfig?.valueDisplayType ?? params.valueDisplayType,
        stacking: isValidStackingValue(yAxisConfig?.stacking) ? yAxisConfig.stacking : undefined
      },
      data: el.data.map((dataPoint) => {
        const { value, ...customData } = dataPoint;

        const isLineChart = chartType === 'line';
        const hasValidValue = typeof value === 'number' && !isNaN(value);
        const shouldBeVisible = !isLineChart || hasValidValue;

        return {
          y: hasValidValue ? value : 0,
          custom: customData,
          visible: shouldBeVisible
        };
      }),
      visible: el.visible ?? true,
      ...(params.colors ? { color: params.colors[index] } : {})
    };
  }) as unknown as T[];
}

export function prepareSeriesDataForSingleYAxis<T extends Highcharts.SeriesOptionsType>(
  params: BaseChartProps,
  chartType: T['type']
): T[] {
  return params.series.map((el, index) => ({
    type: chartType,
    name: el.name ?? params.categories[index],
    custom: {
      displayName: el.displayName,
      id: el.id,
      isPrimaryYAxis: true,
      valueDisplayType: params.valueDisplayType,
      stacking: isValidStackingValue(params.stacking) ? params.stacking : undefined
    },
    data: el.data.map((dataPoint) => {
      const { value, ...customData } = dataPoint;

      const isLineChart = chartType === 'line';
      const hasValidValue = typeof value === 'number' && !isNaN(value);
      const shouldBeVisible = !isLineChart || hasValidValue;

      return {
        y: hasValidValue ? value : 0,
        custom: customData,
        visible: shouldBeVisible
      };
    }),
    visible: el.visible ?? true,
    id: el.id,
    ...(params.colors ? { color: params.colors[index] } : {})
  })) as unknown as T[];
}

export type ChartTypeForClick = 'bar' | 'pie' | 'line';

/**
 * Which Highcharts object carries the clicked element's identity, per chart type.
 *
 * A pie/donut renders one point per slice, so the slice the user clicked *is* the point. Bar and
 * line render one visual element per series, so their points carry no identity of their own and the
 * series holds it instead.
 */
const CLICK_SOURCE_BY_CHART_TYPE: Record<ChartTypeForClick, 'point' | 'series'> = {
  bar: 'series',
  line: 'series',
  pie: 'point'
};

/**
 * Creates a click handler for chart series elements
 * @param handler - The callback to invoke when an element is clicked
 * @param chartType - The type of chart ('bar', 'pie', 'line')
 *   - For bar/line charts: id comes from series level (ctx.series.options.custom?.id)
 *   - For pie/donut charts: id comes from data point level (ctx.options.custom?.id)
 * @param nameAccessorFn - Optional custom function to extract name and id from click context
 */
export function createSeriesElementClickHandler(
  handler?: SeriesElementClickHandler,
  chartType: ChartTypeForClick = 'bar',
  nameAccessorFn?: (ctx: Highcharts.Point) => { name: string; id: string }
): (this: Highcharts.Point) => void {
  return function (this: Highcharts.Point): void {
    // Name, id, and color all come from whichever object owns the clicked element.
    const source: Highcharts.Point | Highcharts.Series =
      CLICK_SOURCE_BY_CHART_TYPE[chartType] === 'point' ? this : this.series;

    const data = nameAccessorFn
      ? nameAccessorFn(this)
      : {
          name: source.name,
          id: source.options.custom?.id ?? ''
        };

    // Highcharts colors can also be gradients or pattern objects, which consumers cannot use as a
    // plain color value, so only solid string colors are reported.
    const color = typeof source.color === 'string' ? source.color : undefined;

    handler?.(color ? { ...data, color } : data);
  };
}
